import AiAssistant from "@/components/assistant/AiAssistant";
import Button from "@/components/common/Button";
import FloatingFAQButton from "@/components/common/FloatingFAQButton";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import {
  AcademicCapIcon,
  Bars3Icon,
  ChartBarIcon,
  DocumentTextIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const featuresRef = useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Build dynamic onboarding tutorial links based on selected language
  const lang = i18n.language?.split("-")[0] || "en";
  const companyOnboardingUrl = `https://minio.gnzabe.com/registration-bucket/${lang}/company/story.html`;
  const employeeOnboardingUrl = `https://minio.gnzabe.com/registration-bucket/${lang}/user/story.html`;

  return (
    <div className="flex min-h-screen flex-col bg-white text-gray-900 transition-colors duration-300 dark:bg-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="relative border-b border-gray-200 transition-colors duration-300 dark:border-gray-800">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center">
                <ShieldCheckIcon className="h-8 w-8 text-primary-600 transition-colors duration-300 dark:text-primary-400" />
                <span className="ml-2 text-xl font-bold text-primary-600 transition-colors duration-300 dark:text-primary-400">
                  {t("app.name")}
                </span>
              </Link>
            </motion.div>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden md:flex md:items-center md:space-x-8">
            <a
              href="#features"
              className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              onClick={(e) => {
                e.preventDefault();
                scrollToFeatures();
              }}
            >
              {t("landing.hero.secondaryCta")}
            </a>
            <Link
              to="/tutorials"
              className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {t("onboarding.tutorials.link", { defaultValue: "Tutorials" })}
            </Link>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />

              <button
                onClick={toggleTheme}
                className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label={
                  theme === "dark"
                    ? t("settings.lightMode")
                    : t("settings.darkMode")
                }
              >
                {theme === "dark" ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>

              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button variant="primary">{t("nav.dashboard")}</Button>
                </Link>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link to="/login">
                    <Button variant="outline">{t("auth.login")}</Button>
                  </Link>
                  <Link to="/register-company">
                    <Button variant="primary">{t("auth.register")}</Button>
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <motion.div
          className={`fixed inset-0 z-[100] md:hidden ${
            mobileMenuOpen ? "block" : "hidden"
          }`}
          initial={{ opacity: 0 }}
          animate={{
            opacity: mobileMenuOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu content */}
          <motion.div
            className="absolute right-0 top-0 h-full w-full max-w-sm bg-white p-6 shadow-xl transition-colors dark:bg-gray-900"
            initial={{ x: "100%" }}
            animate={{ x: mobileMenuOpen ? 0 : "100%" }}
            transition={{ duration: 0.3 }}
          >
            <div className="space-y-1">
              <a
                href="#features"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToFeatures();
                  setMobileMenuOpen(false);
                }}
              >
                {t("landing.hero.secondaryCta")}
              </a>

              <Link
                to="/tutorials"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("onboarding.tutorials.link", { defaultValue: "Tutorials" })}
              </Link>

              <div className="flex items-center space-x-2 px-3 py-2">
                <LanguageSwitcher />

                <button
                  onClick={toggleTheme}
                  className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label={
                    theme === "dark"
                      ? t("settings.lightMode")
                      : t("settings.darkMode")
                  }
                >
                  {theme === "dark" ? (
                    <SunIcon className="h-5 w-5" />
                  ) : (
                    <MoonIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="block rounded-md bg-primary-500 px-3 py-2 text-center text-base font-medium text-white transition-colors hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.dashboard")}
                </Link>
              ) : (
                <div className="space-y-2 px-3 py-2">
                  <Link
                    to="/login"
                    className="block rounded-md border border-gray-300 px-3 py-2 text-center text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("auth.login")}
                  </Link>
                  <Link
                    to="/register-company"
                    className="block rounded-md bg-primary-500 px-3 py-2 text-center text-base font-medium text-white transition-colors hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("auth.register")}
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </header>

      <main className="flex-1">
        {/* Hero section - Remains dark in both modes for consistency */}
        <section className="relative min-h-screen overflow-hidden bg-gray-900 text-white">
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            <video
              src="https://cdn.pixabay.com/video/2020/08/21/47802-451812879_large.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-full object-cover opacity-50"
            >
              <source
                src="https://cdn.pixabay.com/video/2020/08/21/47802-451812879_large.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gray-900/90" />
          </div>

          {/* Content */}
          <div className="relative z-0 mx-auto flex min-h-screen flex-col items-start justify-center px-4 py-20 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-8"
              >
                <div className="space-y-4">
                  <motion.h1
                    className="text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <span className="block bg-gradient-to-r from-white via-primary-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                      {t("landing.hero.title")}
                    </span>
                    <span className="mt-4 block bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent drop-shadow-lg">
                      {t("app.tagline")}
                    </span>
                  </motion.h1>
                  <motion.p
                    className="mt-6 max-w-2xl text-lg font-medium text-gray-300 sm:text-xl md:text-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {t("landing.hero.subtitle")}
                  </motion.p>
                </div>
              </motion.div>

              <motion.div
                className="mt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className="flex flex-col items-start space-y-4 sm:flex-row sm:space-x-6 sm:space-y-0">
                  <Link to="/register-company" className="w-full sm:w-auto">
                    <Button
                      variant="primary"
                      size="lg"
                      className="group relative w-full overflow-hidden rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-3 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary-500/25 sm:w-auto"
                    >
                      <span className="relative z-10">
                        {t("landing.hero.cta")}
                      </span>
                      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary-600 to-primary-700 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <div className="absolute -inset-1 -z-20 rounded-lg bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 blur transition-opacity duration-300 group-hover:opacity-100" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={scrollToFeatures}
                    className="group relative w-full overflow-hidden rounded-lg border-2 border-white/80 bg-white/10 px-6 py-3 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-white hover:bg-white/20 hover:shadow-lg hover:shadow-white/10 sm:w-auto"
                  >
                    <span className="relative z-10">
                      {t("landing.hero.secondaryCta")}
                    </span>
                    <div className="absolute inset-0 -z-10 bg-white/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="absolute -inset-1 -z-20 rounded-lg bg-gradient-to-r from-white/20 to-white/10 opacity-0 blur transition-opacity duration-300 group-hover:opacity-100" />
                  </Button>
                  <div className="flex items-center space-x-3 text-sm text-white/80 sm:ml-2">
                    <span className="hidden sm:inline">|</span>
                    <a
                      href={companyOnboardingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-white/40 underline-offset-4 hover:text-white hover:decoration-white"
                    >
                      Company onboarding
                    </a>
                    <span className="text-white/40">/</span>
                    <a
                      href={employeeOnboardingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-white/40 underline-offset-4 hover:text-white hover:decoration-white"
                    >
                      Employee onboarding
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Decorative elements */}
            <div className="absolute inset-0 -z-10">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500/20 blur-[100px]"
                style={{ width: "80vw", height: "80vh" }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary-500/20 blur-[100px]"
                style={{ width: "60vw", height: "60vh" }}
              />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-transparent via-gray-900/50 to-gray-900" />
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 transform"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <button
              onClick={scrollToFeatures}
              className="group flex flex-col items-center text-white/80 transition-colors hover:text-white"
            >
              <span className="mb-2 text-sm font-medium tracking-wide">
                {t("landing.hero.secondaryCta")}
              </span>
              <svg
                className="h-6 w-6 animate-bounce transition-transform group-hover:scale-110"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </button>
          </motion.div>
        </section>

        {/* Features section */}
        <section
          ref={featuresRef}
          id="features"
          className="relative overflow-hidden bg-white py-24 transition-colors duration-300 dark:bg-gray-900 sm:py-32"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary-500/5 via-transparent to-transparent dark:from-primary-500/10" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 transition-colors dark:text-white sm:text-4xl md:text-5xl">
                <span className="bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent drop-shadow-lg">
                  {t("landing.features.title")}
                </span>
              </h2>
            </motion.div>

            <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: AcademicCapIcon,
                  title: t("landing.features.feature1.title"),
                  description: t("landing.features.feature1.description"),
                  color: "from-primary-500 to-primary-600",
                },
                {
                  icon: DocumentTextIcon,
                  title: t("landing.features.feature2.title"),
                  description: t("landing.features.feature2.description"),
                  color: "from-secondary-500 to-secondary-600",
                },
                {
                  icon: ChartBarIcon,
                  title: t("landing.features.feature3.title"),
                  description: t("landing.features.feature3.description"),
                  color: "from-accent-500 to-accent-600",
                },
                {
                  icon: ShieldCheckIcon,
                  title: t("landing.features.feature4.title"),
                  description: t("landing.features.feature4.description"),
                  color: "from-success-500 to-success-600",
                },
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="group relative rounded-2xl border border-gray-200 bg-gray-50 p-8 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-500/10 dark:border-white/10 dark:bg-white/5"
                >
                  <div
                    className={`absolute -top-4 left-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${feature.color} text-white shadow-lg transition-transform group-hover:scale-110`}
                  >
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-8 text-lg font-semibold text-gray-900 transition-colors dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 transition-colors dark:text-gray-300">
                    {feature.description}
                  </p>
                  <div className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-r from-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-white/5" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial section */}
        <section className="bg-white py-16 transition-colors duration-300 dark:bg-gray-900 sm:py-24 lg:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 transition-colors dark:text-white sm:text-4xl">
                {t("landing.testimonials.title")}
              </h2>
            </motion.div>
            <div className="mt-16">
              <motion.blockquote
                className="mx-auto max-w-2xl text-center text-xl font-medium leading-relaxed text-gray-900 transition-colors dark:text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <p>
                  &ldquo;{t("landing.testimonials.testimonial1.quote")}&rdquo;
                </p>
                <footer className="mt-8">
                  <div className="md:flex md:items-center md:justify-center">
                    <div className="md:flex-shrink-0">
                      <div className="mx-auto h-10 w-10 rounded-full bg-primary-300 text-center text-xl font-semibold text-primary-800">
                        SJ
                      </div>
                    </div>
                    <div className="mt-3 text-center md:ml-4 md:mt-0 md:flex md:items-center">
                      <div className="text-base font-medium text-primary-600 transition-colors dark:text-primary-100">
                        {t("landing.testimonials.testimonial1.author")}
                      </div>
                      <div className="text-base font-medium text-primary-500 transition-colors dark:text-primary-200">
                        <span className="mx-1">&middot;</span>
                        {t("landing.testimonials.testimonial1.role")}
                      </div>
                    </div>
                  </div>
                </footer>
              </motion.blockquote>
            </div>
          </div>
        </section>

        {/* CTA section */}
        <section className="bg-white py-16 transition-colors duration-300 dark:bg-gray-900 sm:py-24 lg:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-2xl bg-primary-600 px-6 py-10 shadow-xl sm:px-12 sm:py-20">
              <div className="relative lg:grid lg:grid-cols-2 lg:gap-8">
                <div className="mx-auto max-w-md lg:mx-0 lg:max-w-none">
                  <motion.h2
                    className="text-3xl font-bold tracking-tight text-white sm:text-4xl"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    {t("landing.cta.title")}
                  </motion.h2>
                  <motion.p
                    className="mt-6 text-lg text-primary-100"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {t("landing.cta.subtitle")}
                  </motion.p>
                </div>
                <div className="mt-12 sm:mt-16 lg:mt-0 lg:flex lg:items-center lg:justify-end">
                  <motion.div
                    className="rounded-md shadow"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Link
                      to="/register-company"
                      className="flex items-center justify-center rounded-md border border-transparent bg-white px-5 py-3 text-base font-medium text-primary-600 transition-colors hover:bg-gray-50"
                    >
                      {t("landing.cta.button")}
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white transition-colors duration-300 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex items-center space-x-6 md:order-2">
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-gray-500 dark:hover:text-gray-300"
              >
                <span className="sr-only">LinkedIn</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-gray-500 dark:hover:text-gray-300"
              >
                <span className="sr-only">Twitter</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-gray-500 dark:hover:text-gray-300"
              >
                <span className="sr-only">GitHub</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
            <div className="mt-8 md:order-1 md:mt-0">
              <div className="flex flex-col items-center space-y-2 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
                <p className="text-center text-base text-gray-500 transition-colors dark:text-gray-400">
                  &copy; {new Date().getFullYear()} Gnzabe. All rights reserved.
                </p>
                <p className="text-center text-sm text-gray-400 transition-colors dark:text-gray-500">
                  Powered by{" "}
                  <a
                    href="https://cctechet.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                  >
                    CCTechet
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* AI Assistant */}
      <AiAssistant />

      {/* Floating FAQ Button */}
      <FloatingFAQButton />
    </div>
  );
};

export default LandingPage;
